import { api } from './client'

// ============================================================================
// Physical Stocktake — kiểm kê thực tế cross 5 kind lô.
// Spec: docs/superpowers/specs/2026-07-04-stocktake-design.md
// ============================================================================

export type StocktakeKind =
    'sorting' | 'scenting' | 'packaging' | 'product' | 'agri_materials'

export type StocktakeStatus =
    'draft' | 'counting' | 'pending_approval' | 'approved' | 'cancelled'

// Phiên kiểm — chiều T (evening, chốt sổ) hoặc sáng T+1 (morning, auto-chụp).
// Xem docs/superpowers/specs/2026-07-06-stocktake-2-sessions-design.md.
export type StocktakeSessionTime = 'evening' | 'morning'

export type StocktakeVarianceReason =
    'correction' | 'damaged' | 'expired' | 'lost' | 'other'

export interface StocktakeLine {
    id: string
    session_id: string
    lot_id: string
    lot_code: string
    warehouse_id: string
    warehouse_name: string
    material_or_product_name?: string
    unit?: string
    system_qty: number
    delta_after_snapshot: number
    expected_qty: number
    counted_qty?: number | null
    variance: number
    variance_reason?: StocktakeVarianceReason | null
    line_notes?: string | null
    adjustment_in_note_id?: string | null
    adjustment_out_note_id?: string | null
    counted_at?: string | null
    counted_by_id?: string | null
    created_at: string
    // BE cờ true khi lô có PNK/PXK approved trong ngày Asia/Ho_Chi_Minh
    // của session kind. FE dùng cho filter chip "có/không biến động".
    has_activity_today?: boolean
}

export interface StocktakeSession {
    id: string
    code: string
    kind: StocktakeKind
    session_date: string
    session_time: StocktakeSessionTime
    snapshot_batch_id: string
    status: StocktakeStatus
    notes?: string | null
    created_by_id: string
    created_by_name?: string
    submitted_at?: string | null
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    cancelled_at?: string | null
    total_lines: number
    variance_lines: number
    lines?: StocktakeLine[]
    created_at: string
    updated_at: string
}

export interface CreateStocktakeRequest {
    kind: StocktakeKind
    session_time: StocktakeSessionTime
    // Required cho evening (dùng snapshot chốt sổ). Bỏ trống cho morning
    // (hệ thống tự tạo internal snapshot cho cycle T = ngày trước hôm bấm).
    snapshot_batch_id?: string | null
    notes?: string
}

export interface UpdateLineRequest {
    counted_qty?: number | null
    variance_reason?: StocktakeVarianceReason | null
    line_notes?: string | null
}

export interface ApproveResponse {
    session: StocktakeSession
    pnk_note_id?: string | null
    pxk_note_id?: string | null
}

export interface ListStocktakeParams {
    kind?: StocktakeKind
    date?: string
    status?: StocktakeStatus
    session_time?: StocktakeSessionTime
    page?: number
    per_page?: number
}

const BASE = '/stocktake-sessions'

export const stocktakeApi = {
    list: (params?: ListStocktakeParams) =>
        api.get<StocktakeSession[]>(BASE, { params }),
    get: (id: string) => api.get<StocktakeSession>(`${BASE}/${id}`),
    create: (data: CreateStocktakeRequest) =>
        api.post<StocktakeSession>(BASE, data),
    updateLine: (sessionId: string, lineId: string, data: UpdateLineRequest) =>
        api.patch<StocktakeLine>(`${BASE}/${sessionId}/lines/${lineId}`, data),
    submit: (id: string) => api.post<void>(`${BASE}/${id}/submit`),
    approve: (id: string) => api.post<ApproveResponse>(`${BASE}/${id}/approve`),
    cancel: (id: string) => api.post<void>(`${BASE}/${id}/cancel`),
    refreshDelta: (id: string) =>
        api.post<StocktakeSession>(`${BASE}/${id}/refresh-delta`),
}
